import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import PaymentQRModal from '../../components/PaymentQRModal';
import './MyPaymentsPage.css';
import { translateError } from '../../utils/errorMessages';

interface OutstandingItem {
  memberId: number;
  memberName: string;
  type: 'event' | 'membership';
  id: number;
  name: string;
  amount: number;
  paidAmount: number;
  balance: number;
  dueDate?: string;
  durationDays?: number;
  periodStart?: string;
  periodEnd?: string;
}

interface PaidItem {
  memberId: number;
  memberName: string;
  id: number;
  date: string;
  amount: number;
  description: string | null;
  eventName?: string;
  membershipName?: string;
}

type TabFilter = 'outstanding' | 'paid';

const MyPaymentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [outstanding, setOutstanding] = useState<OutstandingItem[]>([]);
  const [paid, setPaid] = useState<PaidItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabFilter>('outstanding');
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  // QR modal state
  const [qrItem, setQrItem] = useState<OutstandingItem | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.my.outstanding();
      setOutstanding(data.outstanding);
      setPaid(data.paid);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Unique members across both lists
  const members = useMemo(() => {
    const map = new Map<number, string>();
    for (const item of outstanding) map.set(item.memberId, item.memberName);
    for (const item of paid) map.set(item.memberId, item.memberName);
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [outstanding, paid]);

  const filteredOutstanding = useMemo(() => {
    if (selectedMemberId === null) return outstanding;
    return outstanding.filter(item => item.memberId === selectedMemberId);
  }, [outstanding, selectedMemberId]);

  const filteredPaid = useMemo(() => {
    if (selectedMemberId === null) return paid;
    return paid.filter(item => item.memberId === selectedMemberId);
  }, [paid, selectedMemberId]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount: number) => {
    return amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  return (
    <div className="admin-list-page">
      <h2>{t('userDashboard.paymentsTitle')}</h2>

      {/* Status Toggle */}
      <div className="status-toggle" style={{ display: 'flex', marginBottom: '1rem' }}>
        <button
          onClick={() => setTab('outstanding')}
          style={{
            display: 'inline-flex',
            padding: '0.5rem 1rem',
            border: '1px solid var(--border-color)',
            borderRadius: '4px 0 0 4px',
            backgroundColor: tab === 'outstanding' ? 'var(--orange-primary)' : 'var(--bg-light)',
            color: tab === 'outstanding' ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            boxShadow: 'none',
          }}
        >
          {t('payments.outstanding')}
        </button>
        <button
          onClick={() => setTab('paid')}
          style={{
            display: 'inline-flex',
            padding: '0.5rem 1rem',
            border: '1px solid var(--border-color)',
            borderLeft: 'none',
            borderRadius: '0 4px 4px 0',
            backgroundColor: tab === 'paid' ? '#27ae60' : 'var(--bg-light)',
            color: tab === 'paid' ? 'white' : 'var(--text-primary)',
            cursor: 'pointer',
            boxShadow: 'none',
          }}
        >
          {t('payments.paidHistory')}
        </button>
      </div>

      {/* Member Filter Chips */}
      {members.length > 1 && (
        <div className="member-filter-chips">
          <button
            className={`member-chip ${selectedMemberId === null ? 'active' : ''}`}
            onClick={() => setSelectedMemberId(null)}
          >
            {t('userDashboard.allMembers')}
          </button>
          {members.map(m => (
            <button
              key={m.id}
              className={`member-chip ${selectedMemberId === m.id ? 'active' : ''}`}
              onClick={() => setSelectedMemberId(m.id)}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      {error && <p className="error-message">{t('common.error')}: {error}</p>}

      {loading ? (
        <p>{t('common.loading')}</p>
      ) : tab === 'outstanding' ? (
        filteredOutstanding.length === 0 ? (
          <p className="empty-state">{t('userDashboard.noOutstandingPayments')}</p>
        ) : (
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('paymentOverview.member')}</th>
                <th>{t('common.type')}</th>
                <th>{t('common.name')}</th>
                <th>{t('memberships.period')}</th>
                <th>{t('userDashboard.balance')}</th>
                <th>{t('payments.date')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOutstanding.map((item) => (
                <tr key={`${item.type}-${item.id}-${item.memberId}`}>
                  <td>{item.memberName}</td>
                  <td>
                    <span className={`type-badge type-badge--${item.type}`}>
                      {item.type === 'event' ? t('paymentOverview.event') : t('payments.membership')}
                    </span>
                  </td>
                  <td>{item.name}</td>
                  <td>
                    {item.type === 'membership' && item.periodStart && item.periodEnd
                      ? `${formatDate(item.periodStart)} – ${formatDate(item.periodEnd)}`
                      : '-'}
                  </td>
                  <td className="amount-cell">{formatAmount(item.balance)} CZK</td>
                  <td>{item.dueDate ? formatDate(item.dueDate) : '-'}</td>
                  <td>
                    <button
                      className="action-btn qr-btn"
                      onClick={() => setQrItem(item)}
                      title={t('payments.generateQR')}
                      style={{
                        padding: '0.25rem 0.75rem',
                        backgroundColor: 'var(--orange-primary)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      QR
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      ) : (
        filteredPaid.length === 0 ? (
          <p className="empty-state">{t('userDashboard.noPaidPayments')}</p>
        ) : (
          <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('paymentOverview.member')}</th>
                <th>{t('payments.date')}</th>
                <th>{t('payments.description')}</th>
                <th>{t('payments.amount')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPaid.map((item) => (
                <tr key={item.id}>
                  <td>{item.memberName}</td>
                  <td>{formatDate(item.date)}</td>
                  <td>{item.eventName || item.membershipName || item.description || '-'}</td>
                  <td className="amount-cell">{formatAmount(item.amount)} CZK</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )
      )}

      {/* QR Modal */}
      {qrItem && (
        <PaymentQRModal
          isOpen={!!qrItem}
          onClose={() => setQrItem(null)}
          type={qrItem.type}
          entityId={qrItem.id}
          entityName={qrItem.name}
          memberId={qrItem.memberId}
          memberName={qrItem.memberName}
          amount={qrItem.balance}
          durationDays={qrItem.durationDays}
        />
      )}
    </div>
  );
};

export default MyPaymentsPage;
